import {decodeJWT} from '@/utils/jwt';
/**
 * @typedef {import('vuex').ActionContext<UserState>} ActionContext
 * @typedef {import('@/utils/jwt').AccessTokenData} AccessTokenData
 * @typedef {import('@/types').ApiResponse} ApiResponse
 * @typedef {import('../').RootState} RootState
 * @typedef {import('./types').UserState} UserState
 * @typedef {import('./types').UserProfile} UserProfile
 * @typedef {import('./types').TokenData} TokenResponse
 * @typedef {import('@/types').Api.Dashboard} DashboardData
 * @typedef {import('@/types').ApiResponse<DashboardData>} DashboardResult
 * @typedef {import('@/types').Api.Result} SummaryData
 * @typedef {import('@/types').ApiResponse<SummaryData>} SummaryResult
 */

/** LocalStorage item keys */
const LSKey = {
	AcessToken: 'user.accessToken',
	RefreshToken: 'user.refreshToken',
  DarkTheme: 'user.darkTheme',
  Profile: 'user.profile',
  Uuid: 'user.uuid',
};

/** @type {import('vuex').Module<UserState, RootState>} */
const vuexModule = {
    namespaced: true,
    /** @return {UserState} */
    state: () => ({
      locale: 'en',
      accessToken: localStorage.getItem(LSKey.AcessToken),
      refreshToken: localStorage.getItem(LSKey.RefreshToken),
      uuid: localStorage.getItem(LSKey.Uuid) || null,
      darkTheme: localStorage.getItem(LSKey.DarkTheme) === '1',
      profile: null,
    }),

    actions: {
      async initialize({state, dispatch, commit, getters}) {
        const {uuid, accessToken} = state;
        const tokenJson = !accessToken ? null : decodeJWT(accessToken);

        if (!uuid || !tokenJson) { // unknown user or token invalid
          commit('uuid', getters.uuid);
          return commit('token', null), null;
        }

        // if lifetime less than 4 hours - refresh token
        const lifetime = tokenJson.expires.getTime() - Date.now();
        console.log(`token lifetime: ${Math.round(lifetime / 60e3)} minutes`);
        if (lifetime < (4 * 3600e3)) await dispatch('refreshToken');
        else {
          // if accessToken is OK
          // restore profile from localStorage to speed up application launch
          let profile = localStorage.getItem(LSKey.Profile);
          if (profile = JSON.parse(profile || null)) {
            commit('profile', profile);
          }
        }

        // then request profile update
        if (state.refreshToken) {
          if (!state.profile) {
            const {code, error, data} = await dispatch('profile');
            if (code === 403 && await dispatch('refreshToken')) await dispatch('profile');
          } else dispatch('profile').then(async (response) => {
            const {code, error, data} = response;
            if (code === 403 && await dispatch('refreshToken')) await dispatch('profile');
          });
        }

        return state.profile || null;
      },

      /**
       * @param {ActionContext} context
       * @param {boolean} enabled
       */
      darkTheme({state, commit}, enabled) {
          commit('darkTheme', enabled === undefined ? !state.darkTheme : enabled);
      },

      /** User login
       * @param {ActionContext} context
       * @param {{email:string; password:string;}} user
       * @return {Promise<ApiResponse<(null|UserProfile)>>}
       */
      async login({dispatch, commit, getters, rootGetters}, {login, password}) {

        const payload = {
          user: {email: login, password},
          device: getters.device,
        };

        let data = null;
        let error = '';
        /** @type {import('axios').AxiosInstance} */
        const client = rootGetters.$client;
        /** @type {(TokenResponse|null)} */
        const result = await client.post('/token/', payload)
          .then(res => res.status !== 200 ? (error = `Error (${res.status})`, null) : res.data)
          .catch(err => (error = err.toString(), err.response?.data));

        if (error) {
          const {detail, email, password} = result || {};
          const message = password || email || detail;
          return {data:null, error: message || error};
        }

        commit('token', result);

        const {data: profile, error: err} = await dispatch('profile');
        if (err) error = err;
        else data = profile;

        return {data, error};
      },

      /** New user registration
       * @param {ActionContext} context
       * @param {{email:string; password:string;}} user
       * @return {Promise<ApiResponse<(null|UserProfile)>>}
       */
      async signup({dispatch, commit, getters, rootGetters}, {login, password}) {
        const payload = {
          user: {email: login, password},
          device: getters.device,
        };

        let error = '';
        const client = rootGetters.$client;
        /** @type {(TokenResponse|null)} */
        const result = await client.post('/signup/', payload)
          .then(res => res.status === 201 ? res.data : (error = `Error (${res.status})`, null))
          .catch(err => (error = err.toString(), err.response?.data));

        if (error) {
          const {password, email, non_field_errors} = result || {};
          const message = password || email || non_field_errors;
          return {data:null, error: message || error};
        }

        commit('token', result);
        const {data} = await dispatch('profile');
        return {data, error};
      },

      async refreshToken({state, commit, getters, rootGetters}) {

        const tokenJson = decodeJWT(state.refreshToken);
        if (!tokenJson) return commit('token', null), false;

        const lifetime = tokenJson.expires.getTime() - Date.now();
        if (lifetime < 0) return commit('token', null), false;

        /** @type {import('axios').AxiosInstance} */
        const client = rootGetters.$client;
        let error = '';
        const data = {
          token: {refresh: state.refreshToken},
          device: getters.device,
        };

        /** @type {(TokenResponse|null)} */
        const result = await client.post('/token/refresh/', data)
          .then(res => res.status !== 200 ? (error = `Error (${res.status})`, null) : res.data)
          .catch(err => (error = err.response?.data?.detail || err.toString(), null));
        commit('token', result);

        if (!result) console.error('token', error);
        return !!result;
      },

      async passwordReset({getters, rootGetters}, {login}) {
        let error = '';
        const client = rootGetters.$client;
        const params = {
          app_version: getters.device.app_version,
          email: login,
        };

        const result = await client.post('/password/reset/', params)
          .then(res => res.status === 200 ? res.data : null)
          .catch(err => (error = err.toString(), null));

        console.log('RESET', error || result);
        return {error, data: result};
      },

      /** Logout user
       * @param {ActionContext} context
       * @return {Promise<ApiResponse<(UserProfile|null)>>}
       */
      logout({commit}) {
        commit('token', null);
        commit('profile', null);
      },

      /** Fetch user profile data
       * @param {ActionContext} context
       * @return {Promise<ApiResponse<(UserProfile|null)>>}
       */
      async profile({commit, rootGetters}) {
        let error = '';
        let code = 200;
        const client = rootGetters.$client;
        /** @type {UserProfile|null} */
        const data = await client.get('/profile/')
          .then(res => {
            if (res.status === 200) return res.data
            error = `Error ${code = res.status}`;
            return null;
          })
          .catch(err => {
            error = err.toString();
            code = err.response?.status || 0;
            return null;
          });

        if (!data) console.error(code, error);
        else commit('profile', data);
        return {data, error, code};
      },

      /** Update user profile data
       * @param {ActionContext} context
       * @param {UserProfile} payload
       * @return {Promise<ApiResponse<(UserProfile|null)>>}
       */
      async profileUpdate({state, commit, rootGetters}, payload = {}) {
        console.log('PATCH profile', payload);
        /** @type {import('axios').AxiosInstance} */
        const client = rootGetters.$client;
        let error = '';
        let code = 200;
        const data = await client.patch('/profile/', payload)
          .then(res => {
            if (res.status === 200) return res.data;
            return error = `Error ${code = res.status}`, null;
          })
          .catch(err => {
            error = err.toString();
            code = err.response?.status || 0;
            return null;
          });

        if (!data) console.error(code, error);
        else {
          console.log('PATCH profile >', data.last_name);
          commit('profile', data);
        }
        return {data, error, code};
      },

      /** Fetch user dashboard data
       * @param {ActionContext} context
       * @return {Promise<DashboardResult>}
       */
      async dashboard({commit, rootGetters}) {
        let error = '';
        let code = 200;
        const client = rootGetters.$client;
        /** @type {DashboardData|null} */
        const data = await client.get('/dashboard/')
          .then(res => {
            if (res.status === 200) return res.data
            return error = `Error ${code = res.status}`, null;
          })
          .catch(err => {
            error = err.toString();
            code = err.response?.status || 0;
            return null;
          });

        if (!data) console.error(code, error);
        else if (data.birth_date) {
          const [dd, mm, yyyy] = data.birth_date.split('.');
          data.birth_date = [yyyy, mm, dd].join('-');
        }
        return {error, code, data};
      },

      /** Update user dashboard data
       * @param {ActionContext} context
       * @param {DashboardData} payload
       * @return {Promise<ApiResponse<(DashboardData|null)>>}
       */
      async dashboardUpdate({state, commit, rootGetters}, payload = {}) {
        console.log('PATCH dashboard', payload);
        /** @type {import('axios').AxiosInstance} */
        const client = rootGetters.$client;
        let error = '';
        let code = 200;
        const data = await client.patch('/dashboard/', payload)
          .then(res => {
            if (res.status === 200) return res.data;
            return error = `Error ${code = res.status}`, null;
          })
          .catch(err => {
            error = err.toString();
            code = err.response?.status || 0;
            return null;
          });

        return {data, error, code};
      },

      /** Fetch user summary data
       * @param {ActionContext} context
       * @return {Promise<SummaryResult>}
       */
      async summary({commit, rootGetters}) {
        let error = '';
        let code = 200;
        const client = rootGetters.$client;
        /** @type {SummaryData|null} */
        const data = await client.get('/resultdata/')
          .then(res => {
            if (res.status === 200) return res.data?.user_calc_data || null;
            return error = `Error ${code = res.status}`, null;
          })
          .catch(err => {
            error = err.toString();
            code = err.response?.status || 0;
            return null;
          });

        if (error) console.error(code, error);
        return {error, code, data};
      },
    },

    mutations: {
      uuid(state,/**@type {string}*/id) {
        if (id) localStorage.setItem(LSKey.Uuid, id);
        else localStorage.removeItem(LSKey.Uuid);
        state.uuid = id || null;
      },

      profile(state,/**@type {UserProfile}*/data) {
        state.profile = data || null;
        console.log('LN', data.last_name);
        if (!data) localStorage.removeItem(LSKey.Profile);
        else localStorage.setItem(LSKey.Profile, JSON.stringify(data));

      },

      token(state,/**@type {TokenResponse}*/token) {

        if (token) {
          localStorage.setItem(LSKey.AcessToken, token.access);
          localStorage.setItem(LSKey.RefreshToken, token.refresh);
        } else {
          localStorage.removeItem(LSKey.AcessToken);
          localStorage.removeItem(LSKey.RefreshToken);
        }

        state.refreshToken = token?.refresh || null;
        state.accessToken = token?.access || null;
      },

      darkTheme(state,/**@type {boolean}*/st) {
        if (st) localStorage.setItem(LSKey.DarkTheme, '1');
        else localStorage.removeItem(LSKey.DarkTheme);
        state.darkTheme = st;
      },
    },

    getters: {
      uuid() {// generate Universally unique identifier
        return ([1e7]+-1e3+-4e3+-8e3+-1e11).replace(/[018]/g, c =>
          (c ^ crypto.getRandomValues(new Uint8Array(1))[0] & 15 >> c / 4).toString(16)
        );
      },

      device: state => ({
        'device_uuid': state.uuid,
        'os_name': navigator.userAgentData.platform,
        'os_version': '',
        'app_version': '1',
        'device_model': 'Web',
      }),
    },
};

export {vuexModule};